import React, { useEffect, useState, useRef } from "react";
import ReactDOM from "react-dom";
import { dataService } from "../../../Services/data/data.service";
import { makeStyles } from "@material-ui/core/styles";
import { ChevronDown, Braces, Timer, CircleHelp, List, ListTodo, Hash } from "lucide-react";
import { IconButton, Select, FormControl, MenuItem, InputLabel, ListSubheader, Tooltip } from "@mui/material";
import { externalApiTypes } from "../../../Config/constants";

function DropdownType({ setTypeAction, allExternalApis, allDataLists, error, ...props }) {
  const classes = useStyles();
  const [select, setSelect] = useState(null);
  const [list, setList] = useState([]);
  const [apis, setapis] = useState([]);
  const [openList, setOpenList] = useState(false);
  const [openApi, setOpenApi] = useState(false);

  // Dropdown
  const [openDropdown, setOpenDropdown] = useState(false);
  const [selectedTypeName, setSelectedTypeName] = useState(null);
  const triggerRef = useRef(null);
  const contentRef = useRef(null);
  const [position, setPosition] = useState(null);

  const allTypes = [
    { id: 1, name: "Counter" },
    { id: 3, name: "Free text" },
    { id: 5, name: "Scanner" },
    { id: 2, name: "Stopwatch" },
    { id: 6, name: "Take Picture" },
  ];

  useEffect(() => {
    setList(allDataLists);
    setapis(allExternalApis);

    if (props.row !== undefined) {
      let row = props.row;

      if (row.typeId !== null) {
        if (row.typeId === 7) {
          // set selectedTypeName to the corresponding api
          const selectedApi = allExternalApis.find((api) => api.id === row.externalApiId);
          if (selectedApi) {
            setSelectedTypeName(selectedApi.name);
          }
        } else if (row.typeId === 4) {
          // set selectedTypeName to corresponding dataList
          const selectedDataList = allDataLists.find((list) => list.id === row.dataListId);
          if (selectedDataList) {
            setSelectedTypeName(selectedDataList.name);
          }
        } else {
          setSelect(row.typeId);
        }
      }
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (select !== null) {
      const type = allTypes.find((type) => type.id === select);
      if (type) {
        setSelectedTypeName(type.name);
      } else {
        setSelectedTypeName(null);
      }
    }
  }, [select]);

  // Watch for changes in the data props (when data loads after component mounts)
  useEffect(() => {
    setList(allDataLists || []);
    setapis(allExternalApis || []);
  }, [allDataLists, allExternalApis]);

  // Position dropdown like SelectContent
  useEffect(() => {
    if (!openDropdown) return;
    if (!triggerRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();
    setPosition({
      top: rect.bottom + window.scrollY,
      left: rect.left + window.scrollX,
      width: rect.width,
    });

    const handleClickOutside = (event) => {
      if (contentRef.current && !contentRef.current.contains(event.target) && !triggerRef.current.contains(event.target)) {
        setOpenDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);

    function updatePosition() {
      const rect = triggerRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [openDropdown]);

  const handleSelect = (event) => {
    setSelect(event.target.value);
    props.action(event, props.id);
  };

  const toggleOpen = () => setOpenDropdown(!openDropdown);
  const close = () => setOpenDropdown(false);

  const handleTypeSelection = (typeId) => {
    setSelect(typeId);
    setTypeAction(typeId, props.id);
    close();
  };

  const handleApiSelection = (id, row) => {
    setSelectedTypeName(row.name);
    props.actionExternalApi(id, row);
    close();
  };

  const handleDataListSelection = (id, row) => {
    setSelectedTypeName(row.name);
    props.actionDataList(id, row);
    close();
  };

  const handleOpenList = () => {
    setOpenList(!openList);
  };

  const handleOpenApi = () => {
    setOpenApi(!openApi);
  };

  const getApiTypeIcon = (externalApiTypeId) => {
    switch (externalApiTypeId) {
      case externalApiTypes.DATA:
        return <Braces className={classes.itemIcon} />;
      case externalApiTypes.STOPWATCH:
        return <Timer className={classes.itemIcon} />;
      case externalApiTypes.NUMERIC:
        return <Hash className={classes.itemIcon} />;
      default:
        return <CircleHelp className={classes.itemIcon} />;
    }
  };

  const getListTypeIcon = (isMultiSelect) => {
    switch (isMultiSelect) {
      case true:
        return <ListTodo className={classes.itemIcon} />;
      case false:
        return <List className={classes.itemIcon} />;
      default:
        return <ListTodo className={classes.itemIcon} />;
    }
  };

  return (
    <div className={classes.container}>
      {/* Trigger matching SelectTrigger */}
      <div ref={triggerRef} className={`${classes.trigger} ${error ? classes.triggerError : ""}`} onClick={toggleOpen} style={{ height: 40, padding: "8px 12px" }}>
        <span className={selectedTypeName ? classes.placeholder : classes.placeholderText}>{selectedTypeName || "Select a type..."}</span>
        <ChevronDown className={`${classes.icon} ${openDropdown ? classes.iconOpen : ""}`} />
      </div>

      {/* Content matching SelectContent */}
      {openDropdown &&
        position &&
        ReactDOM.createPortal(
          <div
            ref={contentRef}
            className={classes.content}
            style={{
              top: position.top,
              left: position.left,
              width: position.width,
            }}
          >
            {/* Basic types */}
            {allTypes.map((type, index) => (
              <div className={classes.item} onClick={() => handleTypeSelection(type.id)} key={"Type_" + index} style={{ padding: "8px 12px" }}>
                {type.name}
              </div>
            ))}

            {/* List section */}
            <div className={classes.collapseButton} onClick={() => setOpenList(!openList)}>
              List
              <ChevronDown className={`${classes.collapseIcon} ${openList ? classes.iconOpen : ""}`} />
            </div>
            {openList && (
              <>
                {list && list.length > 0 ? (
                  list
                    .filter((list) => !props.companyId || list.companyId === props.companyId)
                    .map((row, index) => (
                      <Tooltip disableInteractive key={"Dropdown_" + index + "_" + row.id} title={row.name} placement="top">
                        <div className={classes.item} onClick={() => handleDataListSelection(props.id, row)} style={{ padding: "8px 12px" }}>
                          <div className={classes.itemContent}>
                            <div className={classes.iconContainer}>{getListTypeIcon(row.isMultiSelect)}</div>
                            <span className={classes.itemText}>{row.name}</span>
                          </div>
                        </div>
                      </Tooltip>
                    ))
                ) : (
                  <div className={classes.emptyState} style={{ padding: "8px 12px" }}>
                    No lists available
                  </div>
                )}
              </>
            )}

            {/* API section */}
            <div className={classes.collapseButton} onClick={() => setOpenApi(!openApi)}>
              API
              <ChevronDown className={`${classes.collapseIcon} ${openApi ? classes.iconOpen : ""}`} />
            </div>
            {openApi && (
              <>
                {apis && apis.length > 0 ? (
                  apis
                    .filter((api) => !props.companyId || api.companyId === props.companyId)
                    .map((row, index) => (
                      <Tooltip disableInteractive key={"API" + index + "_" + row.id} title={row.name} placement="top">
                        <div className={classes.item} onClick={() => handleApiSelection(props.id, row)} style={{ padding: "8px 12px" }}>
                          <div className={classes.itemContent}>
                            <div className={classes.iconContainer}>{getApiTypeIcon(row.externalApiTypeId)}</div>
                            <span className={classes.itemText}>{row.name}</span>
                          </div>
                        </div>
                      </Tooltip>
                    ))
                ) : (
                  <div className={classes.emptyState} style={{ padding: "8px 12px" }}>
                    No APIs available
                  </div>
                )}
              </>
            )}
          </div>,
          document.body
        )}
    </div>
  );
}

export default DropdownType;

const useStyles = makeStyles({
  container: {
    position: "relative",
    display: "inline-block",
    width: "100%",
  },

  // Trigger styles matching SelectTrigger exactly
  trigger: {
    border: "1px solid #DCDCDC",
    borderRadius: "4px",
    backgroundColor: "#fff",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    justifyContent: "space-between",
    transition: "border-color 0.2s",
    minWidth: "fit-content",
    whiteSpace: "nowrap",
    boxSizing: "border-box",
    width: "100%",
    "&:hover": {
      borderColor: "#888",
    },
  },

  triggerError: {
    borderColor: "#D84949 !important",
    "&:hover": {
      borderColor: "#D84949 !important",
    },
  },

  placeholder: {
    flex: 1,
  },

  placeholderText: {
    flex: 1,
    opacity: 0.6,
  },

  // Content styles matching SelectContent exactly
  content: {
    position: "absolute",
    border: "1px solid #DCDCDC",
    backgroundColor: "#fff",
    zIndex: 1300,
    maxHeight: "200px",
    overflowY: "auto",
    marginTop: "4px",
    borderRadius: "4px",
    boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
    padding: "4px",
    "&::-webkit-scrollbar": {
      width: "4px",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#989898",
      borderRadius: "2px",
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "#F4F4F4",
      borderRadius: "2px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      backgroundColor: "#7C7C7C",
    },
  },

  // Item styles matching SelectItem exactly
  item: {
    cursor: "pointer",
    borderRadius: "4px",
    whiteSpace: "nowrap",
    "&:hover": {
      backgroundColor: "#F8F8F8",
    },
  },

  itemContent: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    width: "100%",
    overflow: "hidden",
  },

  iconContainer: {
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
  },

  itemText: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    minWidth: 0,
    flex: 1,
  },

  // Label styles matching SelectLabel exactly
  label: {
    fontWeight: "normal",
    padding: "2px 8px 0px",
    fontSize: ".75rem",
    color: "#0C0E29",
    opacity: 0.6,
  },

  // Collapsible button styles
  collapseButton: {
    width: "100%",
    minHeight: "32px",
    boxSizing: "border-box",
    display: "flex",
    gap: "8px",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "0px 8px",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "16px",
    fontFamily: "Lato, sans-serif",
    fontWeight: "normal",
    color: "rgba(12, 14, 41, 0.6)",
    backgroundColor: "#FFFFFF",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    "&:hover": {
      backgroundColor: "#F4F4F4",
    },
    "&:active": {
      backgroundColor: "#EDEDED",
    },
    "&:focus": {
      outline: "none",
    },
  },

  collapseIcon: {
    width: "16px",
    height: "16px",
    color: "#0C0E29",
    transition: "transform 0.2s",
  },

  // Icon styles
  icon: {
    width: "16px",
    height: "16px",
    transition: "transform 0.2s",
    cursor: "pointer",
  },

  iconOpen: {
    transform: "rotate(180deg)",
  },

  itemIcon: {
    width: "20px",
    height: "20px",
    strokeWidth: "1.4px",
    color: "#0C0E29",
  },

  emptyState: {
    color: "#989898",
    fontStyle: "italic",
    fontSize: "14px",
  },
});
